/**
 * DEFENSE GRID — Missile-Command-style fixed-screen defender
 * (MVP step 1 — skeleton + cursor + cities + bases).
 *
 * The first cartridge in the catalog to consume the pointer input
 * infrastructure (see apps/shell/src/runtime/input-pointer.ts and
 * the InputState.pointer field). Player aims via mouse / touch and
 * — in step 2 — fires counter-missiles by clicking. Step 1 has no
 * missiles yet; the focus is to ship a complete-feeling FOUNDATION
 * with the texture baked in so the cartridge never feels "MVP-thin"
 * (the JADE TUNNEL / CUBE JUMP lesson).
 *
 * Scene composition (480×270 logical):
 *
 *   y=0    ─────────────────────────────────────────────  sky top
 *          .  .   .    *  .       .     .   .   *   .
 *          twinkling star field with parallax-ish two-tier
 *          stars (near = brighter + faster shimmer, far = dim)
 *
 *          ─────────────  threat target line (y=190) ─────
 *
 *   y=210  ┌─────┐ ┌──┐  ┌────┐ ┌─┐  ┌──┐   ┌────┐
 *          │▮ ▮ ▮│ │▮▮│  │▮  ▮│ │▮│  │▮ ▮│  │▮ ▮ ▮│
 *          │     │ │  │  │    │ │ │  │   │  │     │
 *          city silhouettes (6, varied widths/heights),
 *          windows flicker on a per-window LFO
 *
 *   y=246  ▲▲▲              ▲▲▲              ▲▲▲
 *          base-1 turret    base-2 turret    base-3 turret
 *          (each rotates to aim at cursor)
 *
 *   y=270  ─────────────────────────────────────────────  ground footer
 *
 * Cursor
 * ------
 * Pulsing pale-cyan ring at the pointer position. If the player
 * hasn't moved a pointer yet (sentinel x=-1, y=-1), we hide the
 * crosshair and show a centered "MOVE MOUSE TO AIM" hint instead.
 *
 * Attract-mode demo
 * -----------------
 * After 10s of zero pointer movement AND zero clicks, drive the
 * cursor through a scripted sweep across the 6 cities so the
 * cartridge feels "alive and demonstrating itself" — same pattern
 * CUBE JUMP introduced. Real pointer input snaps back to player
 * control instantly.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
} from '@arcraz/arcadia-types';
import {
  ATTRACT_DIM,
  ATTRACT_PROMPT,
  ATTRACT_TITLE,
  BASE_AMMO_DOT,
  BASE_BODY,
  BASE_DARK,
  BASE_OUTLINE,
  BASE_TURRET,
  BASE_TURRET_TIP,
  CARD_BG,
  CARD_BORDER,
  CITY_BODY,
  CITY_OUTLINE,
  COUNTER_HEAD,
  COUNTER_TARGET_X,
  COUNTER_TRAIL,
  CURSOR_BODY,
  CURSOR_HINT_BG,
  CURSOR_HINT_BORDER,
  CURSOR_HINT_TEXT,
  CURSOR_PULSE,
  EXPLODE_CORE,
  EXPLODE_MID,
  EXPLODE_OUTER,
  GROUND_BOTTOM,
  GROUND_TOP,
  HUD_CITIES_LIT,
  HUD_LABEL,
  HUD_SCORE,
  HUD_WAVE,
  ICBM_HEAD,
  ICBM_TARGET_RETICLE,
  ICBM_TRAIL,
  SKY_HORIZON,
  SKY_TOP,
  STAR_FAR,
  STAR_NEAR,
  WINDOW_DIM,
  WINDOW_LIT,
} from './palette.js';

// ---- Canvas geometry ----
const WIDTH = 480;
const HEIGHT = 270;

// ---- HUD ----
const HUD_TOP_H = 22;

// ---- Layout — cities + bases ----
// Threat-target Y — where ICBMs aim. Above the city tops so the city
// silhouettes have headroom; the kill-the-city moment happens when an
// ICBM crosses this line into a city's bounding box.
const TARGET_LINE_Y = 210;
const GROUND_LINE_Y = 250; // where bases sit
const FOOTER_Y = 270;       // canvas bottom

interface CitySpec {
  /** Left edge of this city's silhouette in logical pixels. */
  x: number;
  /** Width in logical pixels. */
  w: number;
  /** Height in logical pixels above the ground line. */
  h: number;
  /** Cell layout for windows — cols × rows. */
  windowCols: number;
  windowRows: number;
}

// 6 cities of varied widths + heights. Hand-tuned positions so they
// nestle into the playfield with breathing room between them and the
// 3 missile bases. Bases live in the GAPS between cities (left-of-
// city-1, between cities 3+4, right-of-city-6).
const CITIES: readonly CitySpec[] = [
  { x: 50,  w: 28, h: 24, windowCols: 3, windowRows: 4 },
  { x: 90,  w: 36, h: 32, windowCols: 4, windowRows: 5 },
  { x: 140, w: 24, h: 20, windowCols: 3, windowRows: 3 },
  { x: 250, w: 30, h: 28, windowCols: 3, windowRows: 4 },
  { x: 290, w: 38, h: 36, windowCols: 4, windowRows: 6 },
  { x: 340, w: 26, h: 22, windowCols: 3, windowRows: 3 },
];

interface BaseSpec {
  /** Center X in logical pixels. */
  cx: number;
}

/** Cartridge state-machine phase.
 *  - playing: normal gameplay
 *  - wave-clear: WAVE N CLEAR card with bonus breakdown (step 5).
 *    Holds for WAVE_CARD_TICKS, then advances to the next wave.
 *  - death-pause: GAME OVER panel renders for DEATH_PAUSE_TICKS
 *    before isGameOver() returns true. Gives the player a beat to
 *    read the panel before the launcher's initials overlay scrims.
 *  - game-over: panel active, Z restarts after the input lock. */
type Phase = 'playing' | 'wave-clear' | 'death-pause' | 'game-over';

/** A small secondary explosion — fired on ICBM kills (pink-tinted)
 *  and on city destruction (large white-orange). Step 4 introduces
 *  these; step 5 may add ICBM ground-impact bursts. */
interface Burst {
  x: number;
  y: number;
  radius: number;
  /** Duration in ticks. Burst progresses 0..1 across this window;
   *  renderer interpolates radius + alpha. */
  durationTicks: number;
  /** Tick counter from spawn. */
  age: number;
  /** Color palette to use — distinguishes ICBM-kill bursts (pink)
   *  from city-destruction bursts (big white-orange). */
  kind: 'icbm-kill' | 'city-destroy';
}

/** An incoming ICBM falling toward a target city (step 3). Step 4
 *  wires the counter-missile blast-zone collision test that destroys
 *  these mid-flight. */
interface IncomingMissile {
  /** Current head position (logical px). Starts at top edge. */
  x: number;
  y: number;
  /** Index into CITIES of the city this ICBM is aimed at. */
  targetCityIdx: number;
  /** Per-tick velocity (unit vector × per-wave ICBM speed). */
  vx: number;
  vy: number;
  /** Trail — full descent path so the player reads the threat
   *  trajectory at a glance. Newest at index 0. */
  trail: Array<{ x: number; y: number }>;
  /** True once the ICBM reached its target city or fell past the
   *  ground line — flagged for cleanup. Step 4 also flips this on
   *  blast-zone hit. */
  dead: boolean;
}

/** A player counter-missile in flight or detonating. Step 2 introduces
 *  these; step 4 uses the detonation kill zone to destroy ICBMs. */
interface CounterMissile {
  /** Current head position (logical px). */
  x: number;
  y: number;
  /** Target (click point). The missile flies straight here, with a
   *  midflight lift to read as a ballistic arc. */
  targetX: number;
  targetY: number;
  /** Launch origin (base barrel tip) — kept so the arc's lift can
   *  ease in/out across the FULL flight distance, not just the
   *  remaining distance. */
  originX: number;
  originY: number;
  /** Total flight distance at launch. Used to compute the eased
   *  lift parameter (0 at origin → max at midpoint → 0 at target). */
  flightDistance: number;
  /** Per-tick velocity components (set once at launch from a unit
   *  vector × COUNTER_SPEED_PX_PER_TICK). */
  vx: number;
  vy: number;
  /** Phase — flying toward target, then exploding at target. */
  phase: 'flying' | 'exploding';
  /** Tick counter inside the current phase. 0 at phase entry, counts
   *  up. In 'exploding' the renderer drives ring radius from this. */
  phaseTicks: number;
  /** Trail of recent positions (head first), bounded to
   *  COUNTER_TRAIL_TICKS. Newest at index 0. */
  trail: Array<{ x: number; y: number }>;
}

const BASES: readonly BaseSpec[] = [
  { cx: 25 },
  { cx: 200 },
  { cx: 400 },
];
const BASE_W = 28;
const BASE_H = 14;
const BASE_AMMO_START = 10; // counter-missiles per base (used in step 2)

// ---- Stars ----
// Pre-generated star field — positions are fixed but twinkle amplitude
// shimmers per star. Two tiers so the field has parallax-ish depth.
interface Star {
  x: number;
  y: number;
  /** Twinkle phase offset so each star shimmers on its own cycle. */
  phase: number;
  /** 'near' or 'far' — drives base color + shimmer amplitude. */
  near: boolean;
}
const NEAR_STAR_COUNT = 30;
const FAR_STAR_COUNT = 60;

// ---- Cursor ----
const CURSOR_RING_R = 10;
const CURSOR_PULSE_PERIOD = 36; // ticks
// When the pointer has never been touched this session, x/y are -1.
// We render a "MOVE MOUSE TO AIM" hint centered in the playfield
// until the first event arrives.
const SENTINEL_POSITION = -1;

// ---- Counter-missile (step 2) ----
// Player counter-missiles travel from the launching base to the click
// point along a parabolic-feeling arc (linear interpolation with a
// slight upward lift mid-flight reads as ballistic at low res).
//
// Speed: 3 logical px/tick is fast enough to feel responsive without
// teleporting; gives the player a fraction of a second to see the
// trail rise.
const COUNTER_SPEED_PX_PER_TICK = 3;
// Mid-flight lift amount in pixels. Subtracted from the linear-
// interp Y at the midpoint, eased to 0 at endpoints. Gives the
// missile a "lobbed" feel without true physics.
const COUNTER_LIFT_PX = 14;
// Once detonated, the explosion fireball expands then holds at max
// radius before fading. Total ticks the kill zone is live = the
// EXPAND + HOLD portion (the fade tail still RENDERS but no longer
// counts as "this is the kill zone" — step 4 will use that distinction).
const COUNTER_EXPLODE_EXPAND_TICKS = 8;  // 0 → max radius
const COUNTER_EXPLODE_HOLD_TICKS = 12;   // sit at max radius
const COUNTER_EXPLODE_FADE_TICKS = 10;   // fade rings out
const COUNTER_EXPLODE_TOTAL_TICKS =
  COUNTER_EXPLODE_EXPAND_TICKS +
  COUNTER_EXPLODE_HOLD_TICKS +
  COUNTER_EXPLODE_FADE_TICKS;
// Max blast radius (logical px). The disc at this radius is the kill
// zone — any ICBM whose head enters it during EXPAND or HOLD ticks
// is destroyed in step 4.
const COUNTER_BLAST_RADIUS = 18;
// Cyan trail behind the missile. We record one point per tick and
// fade the oldest out at this many ticks back.
const COUNTER_TRAIL_TICKS = 12;
// Screen-flash duration on the detonation frame — a brief WHITE
// scrim that punches the explosion's start.
const COUNTER_FLASH_TICKS = 4;

// ---- Incoming ICBM (step 3, per-wave speed via WAVE_SCHEDULE) ----
// Threats arc straight down from random sky positions to random live
// cities. Slow + visible at wave 1; faster at later waves so the
// player has progressively less reaction time. Per-wave speed is
// looked up in WAVE_SCHEDULE; spawn velocity is built from it in
// spawnIncoming().
// Trail length — we record EVERY position (no decimation) so the
// player sees the full descent path. Bounded at ~600 entries which
// is more than enough for a top-to-bottom trip (270 / 1 = 270 ticks).
const ICBM_TRAIL_MAX_LEN = 320;
// Red-alert reticle pulses around the targeted city while at least
// one ICBM is inbound to it. Period in ticks.
const ICBM_RETICLE_PULSE_PERIOD = 40;

// ---- Wave structure (step 5) ----
// Per-wave difficulty schedule. Wave 1 onboards (slow ICBMs, sparse
// spawns); wave 10 is the win-threshold pressure peak (twice the
// missile speed, 6x the count, half the spawn gap). Step 6's
// TREASURE! cinematic plays after surviving wave 10 with at least
// one city alive.
//
// Per-wave fields:
//   icbmCount — total ICBMs spawned in this wave
//   spawnGapTicks — base spawn cadence (PRNG jitter ±)
//   icbmSpeed — px/tick descent
const WAVE_SPAWN_JITTER_TICKS = 36; // ±0.6s, applied to every wave's base gap

interface WaveDifficulty {
  icbmCount: number;
  spawnGapTicks: number;
  icbmSpeed: number;
}

const WAVE_SCHEDULE: readonly WaveDifficulty[] = [
  { icbmCount: 6,  spawnGapTicks: 90, icbmSpeed: 1.0 }, // wave 1
  { icbmCount: 7,  spawnGapTicks: 80, icbmSpeed: 1.0 }, // wave 2
  { icbmCount: 8,  spawnGapTicks: 70, icbmSpeed: 1.1 }, // wave 3
  { icbmCount: 10, spawnGapTicks: 65, icbmSpeed: 1.2 }, // wave 4
  { icbmCount: 12, spawnGapTicks: 60, icbmSpeed: 1.3 }, // wave 5
  { icbmCount: 14, spawnGapTicks: 55, icbmSpeed: 1.4 }, // wave 6
  { icbmCount: 16, spawnGapTicks: 50, icbmSpeed: 1.5 }, // wave 7
  { icbmCount: 18, spawnGapTicks: 45, icbmSpeed: 1.7 }, // wave 8
  { icbmCount: 20, spawnGapTicks: 40, icbmSpeed: 1.9 }, // wave 9
  { icbmCount: 22, spawnGapTicks: 36, icbmSpeed: 2.1 }, // wave 10 (win)
];

const WIN_WAVE = WAVE_SCHEDULE.length; // 10

function getWaveDifficulty(wave: number): WaveDifficulty {
  // Clamp into the schedule range. Waves > WIN_WAVE shouldn't
  // happen — step 6 routes wave-10 completion to the win path —
  // but if anything goes off-rails just hold at the hardest preset
  // rather than crashing on undefined.
  const idx = Math.max(0, Math.min(WAVE_SCHEDULE.length - 1, wave - 1));
  return WAVE_SCHEDULE[idx] ?? WAVE_SCHEDULE[0]!;
}

// ---- Wave-clear card (step 5) ----
const WAVE_CARD_TICKS = 150; // 2.5s — long enough to read the bonuses
const SURVIVAL_BONUS_PER_CITY = 100;
const AMMO_BONUS_PER_UNUSED = 25;

// ---- Interception + city damage (step 4) ----
// Secondary burst when a counter-missile blast kills an ICBM. Smaller
// + pink-tinted so the player reads "I hit one!" distinct from the
// cyan player blast that caused it.
const ICBM_KILL_BURST_RADIUS = 6;
const ICBM_KILL_BURST_TICKS = 12;
// City-destruction burst — the dramatic "you lost a city" cinematic.
// Big white-orange fireball with screen shake.
const CITY_DESTROY_BURST_RADIUS = 24;
const CITY_DESTROY_BURST_TICKS = 30;
const CITY_DESTROY_SHAKE_TICKS = 18;
const CITY_DESTROY_SHAKE_AMPLITUDE = 5;
// Score per intercepted ICBM. Step 5 will own real scoring; step 4
// just gives the player something to see climbing on the HUD.
const SCORE_PER_KILL = 25;

// ---- Game over (step 4) ----
// CRT-glitch death-pause mirrors CUBE JUMP / JADE TUNNEL: render the
// GAME OVER panel for DEATH_PAUSE_TICKS before isGameOver() goes
// true, so the launcher's initials overlay doesn't scrim over a
// mid-game frame.
const DEATH_PAUSE_TICKS = 36;
const GAME_OVER_INPUT_LOCK_TICKS = 30;
const GLITCH_INTRO_TICKS = 24;

// ---- Attract mode ----
const IDLE_TO_ATTRACT_TICKS = 600; // 10s
// Scripted sweep — the cursor visits each city in order, lingering
// briefly. PRNG-free so determinism is preserved.
const ATTRACT_WAYPOINTS: ReadonlyArray<{ x: number; y: number }> = (() => {
  const pts: { x: number; y: number }[] = [];
  for (const c of CITIES) {
    pts.push({ x: c.x + c.w / 2, y: c.h / 2 + GROUND_LINE_Y - c.h });
  }
  // Loop back through the air to the first city to make the cycle
  // feel like a continuous patrol.
  pts.push({ x: WIDTH / 2, y: 50 });
  return pts;
})();
const ATTRACT_TICKS_PER_LEG = 60; // 1s per leg

export class DefenseGridInstance implements GameInstance {
  // GameContext — used in step 2+ for audio.sfx (counter-launch on
  // click, boom on detonation). Also stashed in step 1's constructor
  // for the star-field PRNG draw.
  private readonly ctx: GameContext;
  // World tick — drives star twinkle, window flicker, cursor pulse.
  private worldTicks = 0;
  // Ticks since the player's last pointer movement OR click. When
  // this exceeds IDLE_TO_ATTRACT_TICKS we drive the scripted sweep.
  private idleTicks = 0;
  // Index into ATTRACT_WAYPOINTS — the cursor interpolates between
  // waypoints[attractLeg] → waypoints[attractLeg+1] over
  // ATTRACT_TICKS_PER_LEG ticks.
  private attractLeg = 0;
  private attractLegTicks = 0;

  // Cursor position. Mirrors InputState.pointer; we cache so the
  // attract sweep can override it without mutating the input snapshot.
  private cursorX = SENTINEL_POSITION;
  private cursorY = SENTINEL_POSITION;
  // True once the player has moved a pointer (or clicked) for the
  // first time. Gates the "MOVE TO AIM" hint.
  private pointerSeen = false;
  // Last pointer position we saw — used to detect movement vs. idle.
  private lastSeenPointerX = SENTINEL_POSITION;
  private lastSeenPointerY = SENTINEL_POSITION;

  // Pre-generated star field. Built once at construction.
  private readonly stars: readonly Star[];

  // Per-city destroyed flag (step 4 sets these; step 1 just allocates).
  private readonly destroyedCities: boolean[];

  // Per-base ammo remaining (step 2 decrements on click).
  private readonly ammo: number[];

  // Counter-missiles in flight or detonating (step 2). Bounded
  // naturally by total ammo across all bases (~30 max at game start).
  private missiles: CounterMissile[] = [];
  // Screen-flash burst on detonation frames — a brief white scrim
  // alpha that punches each explosion's start. Counts down to 0.
  private flashTicks = 0;

  // Incoming ICBMs (step 3). Spawned on a jittered cadence over the
  // course of the current wave; despawn on city impact (step 4 also
  // despawns on counter-missile kill).
  private incoming: IncomingMissile[] = [];
  // Ticks until the next ICBM spawn for the current wave. Counts
  // down each tick; <=0 + spawns remaining triggers a spawn.
  private nextIcbmSpawnTicks = 0;
  // Current wave number (1..WIN_WAVE). HUD reads this. Step 6 fires
  // the TREASURE! win when wave > WIN_WAVE with cities still alive.
  private wave = 1;
  // ICBMs remaining to spawn for the current wave. Decrements on
  // each spawn. When 0 AND `incoming` empty, the wave is "clear"
  // → wave-clear card → advance to next wave.
  private icbmsRemainingThisWave = WAVE_SCHEDULE[0]!.icbmCount;
  // Wave-clear bonus accounting — populated when the last ICBM dies.
  // Displayed on the wave-clear card.
  private lastSurvivalBonus = 0;
  private lastAmmoBonus = 0;

  // ---- Step 4 state ----
  // Secondary bursts (ICBM kills + city-destruction cinematics).
  private bursts: Burst[] = [];
  // Score — climbs by SCORE_PER_KILL per intercepted ICBM. Step 5
  // adds proper end-of-wave bonuses.
  private score = 0;
  // Phase state machine. Death-pause holds the GAME OVER panel
  // visible for a beat before the launcher's initials overlay
  // takes over (CUBE JUMP / JADE TUNNEL convention).
  private phase: Phase = 'playing';
  private phaseTicks = 0;
  private gameOverLockTicks = 0;
  private restartArmed = false;
  private finalScore = 0;
  // Per-burst screen shake — count down to 0 over CITY_DESTROY_SHAKE_TICKS.
  private shakeTicks = 0;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    this.stars = buildStarField(ctx);
    this.destroyedCities = CITIES.map(() => false);
    this.ammo = BASES.map(() => BASE_AMMO_START);
    // Stamp the first ICBM spawn delay so wave 1 doesn't fire on
    // tick 0 — the player gets a moment of calm to read the scene.
    this.armNextIcbmSpawn();
  }

  // ---- Cabinet contract surface ----

  input(state: InputState): void {
    // Game-over: Z (fireA) arms a restart once the input lock
    // expires. The cabinet's launcher polls shouldRestart() and
    // resets the round on its own.
    if (this.phase === 'game-over') {
      if (
        this.gameOverLockTicks <= 0 &&
        state.justPressed.has('fireA')
      ) {
        this.restartArmed = true;
      }
      return;
    }

    // Pointer tracking. Two states matter:
    //   1. Sentinel (-1/-1) — pointer has never moved. Hold the hint.
    //   2. Real position — update cursorX/Y, reset idle counter if
    //      the player actually MOVED (not just held still).
    const px = state.pointer.x;
    const py = state.pointer.y;
    if (px === SENTINEL_POSITION || py === SENTINEL_POSITION) {
      // Pointer has never fired. Don't touch cursor; let attract
      // drive (or hint) once IDLE_TO_ATTRACT_TICKS elapses.
      return;
    }
    if (!this.pointerSeen) {
      // First-ever event — flip the gate and seed lastSeen so the
      // very first event doesn't register as "movement" of -1 → px.
      this.pointerSeen = true;
      this.lastSeenPointerX = px;
      this.lastSeenPointerY = py;
      this.cursorX = px;
      this.cursorY = py;
      this.idleTicks = 0;
      return;
    }
    if (px !== this.lastSeenPointerX || py !== this.lastSeenPointerY) {
      this.cursorX = px;
      this.cursorY = py;
      this.lastSeenPointerX = px;
      this.lastSeenPointerY = py;
      this.idleTicks = 0;
      // Real input snaps us out of attract. Reset the sweep so when
      // the player walks away again it starts from the top.
      this.attractLeg = 0;
      this.attractLegTicks = 0;
    }
    if (state.justPressed.has('pointer')) {
      this.idleTicks = 0;
      this.tryLaunchCounterMissile(px, py);
    }
  }

  update(_dt: number): void {
    this.worldTicks++;
    // Shake + flash decay every tick regardless of phase so death-
    // pause renders settle visibly.
    if (this.flashTicks > 0) this.flashTicks--;
    if (this.shakeTicks > 0) this.shakeTicks--;

    if (this.phase === 'wave-clear') {
      this.phaseTicks--;
      this.tickBursts();
      if (this.phaseTicks <= 0) {
        this.advanceToNextWave();
      }
      return;
    }

    if (this.phase === 'death-pause') {
      this.phaseTicks--;
      this.tickBursts();
      if (this.phaseTicks <= 0) {
        this.phase = 'game-over';
        this.phaseTicks = 0;
      }
      return;
    }

    if (this.phase === 'game-over') {
      if (this.gameOverLockTicks > 0) this.gameOverLockTicks--;
      // CRT-glitch animation counter — increments so the renderer
      // can drive scan-line tears off a single tick value.
      this.phaseTicks++;
      return;
    }

    // phase === 'playing'
    this.idleTicks++;
    this.tickCounterMissiles();
    this.tickIncoming();
    this.tickInterception();
    this.tickBursts();
    if (this.idleTicks > IDLE_TO_ATTRACT_TICKS) {
      this.tickAttract();
    }
  }

  render(g: CanvasRenderingContext2D): void {
    // Screen shake — applied via canvas translate around the entire
    // frame so the HUD shakes with the playfield. The cabinet's
    // cabinet is shaking, not just the city silhouettes.
    let shakeX = 0;
    let shakeY = 0;
    if (this.shakeTicks > 0) {
      const decay = this.shakeTicks / CITY_DESTROY_SHAKE_TICKS;
      shakeX = Math.sin(this.worldTicks * 2.7) * CITY_DESTROY_SHAKE_AMPLITUDE * decay;
      shakeY = Math.cos(this.worldTicks * 3.1) * CITY_DESTROY_SHAKE_AMPLITUDE * decay;
    }
    g.save();
    g.translate(shakeX, shakeY);

    drawSky(g);
    drawStarField(g, this.stars, this.worldTicks);
    drawGround(g);
    drawCities(g, this.destroyedCities, this.worldTicks);
    // Red-alert reticles on currently-threatened cities. Drawn
    // UNDER the bases so the cyan bases punch through if the
    // reticle's red ring expands wide enough to overlap. Pulses
    // independently of the ICBM's progress so the player gets a
    // steady visual cue while ICBMs are inbound.
    drawIcbmTargetReticles(g, this.incoming, this.worldTicks);
    drawBases(g, this.ammo, this.cursorX, this.cursorY, this.pointerSeen);

    // Incoming ICBMs — drawn ABOVE bases so the magenta trail
    // overlays the cyan turret housing as it descends. Trail first
    // (full descent path), then head dot.
    for (const m of this.incoming) {
      drawIncomingTrail(g, m);
    }
    for (const m of this.incoming) {
      drawIncomingHead(g, m);
    }

    // Counter-missiles — drawn ABOVE cities/bases so trails and
    // explosions read on top of the ground silhouette. Trails first,
    // then heads, then explosions, so the explosion ring overlays the
    // missile head as it detonates.
    for (const m of this.missiles) {
      drawCounterTrail(g, m);
    }
    for (const m of this.missiles) {
      if (m.phase === 'flying') drawCounterHead(g, m);
      else drawCounterExplosion(g, m);
    }
    // Active blast zone overlays — the kill radius made visible.
    // Subtle outline so the player learns to read it (step 4 uses
    // this disc to test ICBM collision).
    for (const m of this.missiles) {
      if (m.phase === 'exploding') drawCounterBlastZone(g, m);
    }

    // Secondary bursts (ICBM kills + city destructions) drawn on
    // top of cities/missiles so they read as the explosion event.
    for (const b of this.bursts) {
      drawBurst(g, b);
    }

    drawHudTop(g, this.score, this.wave, this.destroyedCities);

    // Cursor or sentinel hint. Hide cursor during game-over / death-
    // pause — the player isn't aiming anymore.
    if (this.phase === 'playing' && this.pointerSeen) {
      drawCrosshair(g, this.cursorX, this.cursorY, this.worldTicks);
    } else if (this.phase === 'playing') {
      drawSentinelHint(g, this.worldTicks);
    }

    // Detonation screen flash — drawn LAST so it punches over the
    // whole frame. Brief and dim so it adds impact without blinding
    // the player on each shot.
    if (this.flashTicks > 0) {
      const alpha = (this.flashTicks / COUNTER_FLASH_TICKS) * 0.25;
      g.fillStyle = `rgba(255, 255, 255, ${alpha})`;
      g.fillRect(0, 0, WIDTH, HEIGHT);
    }

    // Attract banner — only when the demo is actively driving.
    if (this.phase === 'playing' && this.idleTicks > IDLE_TO_ATTRACT_TICKS) {
      drawAttractBanner(g, this.worldTicks);
    }

    // Wave-clear card — celebrates the survived wave + shows the
    // bonus breakdown. Slides over from the right? No, keep it
    // simple: scrim + centered card, mirrors CUBE JUMP's PYRAMID
    // CLEAR pattern. The renderer reads phaseTicks countdown to
    // animate the numerals in a typewriter reveal (so the bonuses
    // feel "earned" as they appear).
    if (this.phase === 'wave-clear') {
      const elapsed = WAVE_CARD_TICKS - this.phaseTicks;
      drawWaveClearCard(
        g,
        this.wave,
        this.lastSurvivalBonus,
        this.lastAmmoBonus,
        this.score,
        elapsed
      );
    }

    // Death-pause + game-over overlays. Death-pause uses the inverse
    // tick count so the glitch animates IN over the pause.
    if (this.phase === 'death-pause') {
      const intoTicks = DEATH_PAUSE_TICKS - this.phaseTicks;
      drawGameOver(g, this.finalScore, intoTicks, GAME_OVER_INPUT_LOCK_TICKS);
    } else if (this.phase === 'game-over') {
      drawGameOver(
        g,
        this.finalScore,
        this.phaseTicks + DEATH_PAUSE_TICKS,
        this.gameOverLockTicks
      );
    }

    g.restore();
  }

  resize(_w: number, _h: number): void {
    // Cabinet's compositor handles display scaling.
  }

  // Cabinet duck-typed convention surface.
  getScore(): number {
    return this.score;
  }
  getFinalScore(): number {
    return this.phase === 'game-over' ? this.finalScore : this.score;
  }
  isGameOver(): boolean {
    return this.phase === 'game-over';
  }
  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }
  pause(): void {
    // No long-lived audio loops yet.
  }
  resume(): void {
    // Nothing to restore.
  }
  teardown(): void {
    // No long-lived resources.
  }

  // ---- Private helpers ----

  /**
   * Drive the attract-mode scripted sweep. Interpolates cursor between
   * consecutive waypoints over ATTRACT_TICKS_PER_LEG ticks each, loops
   * forever. Snaps off the moment a real pointer event arrives.
   */
  private tickAttract(): void {
    const a = ATTRACT_WAYPOINTS[this.attractLeg % ATTRACT_WAYPOINTS.length];
    const b = ATTRACT_WAYPOINTS[(this.attractLeg + 1) % ATTRACT_WAYPOINTS.length];
    if (a === undefined || b === undefined) return;
    this.attractLegTicks++;
    const t = this.attractLegTicks / ATTRACT_TICKS_PER_LEG;
    this.cursorX = a.x + (b.x - a.x) * t;
    this.cursorY = a.y + (b.y - a.y) * t;
    if (this.attractLegTicks >= ATTRACT_TICKS_PER_LEG) {
      this.attractLegTicks = 0;
      this.attractLeg++;
    }
    // Force the hint off during attract — we want the player to see
    // the cartridge in motion, not the static "MOVE TO AIM" card.
    this.pointerSeen = true;
  }

  // ---- Counter-missile (step 2) ----

  /**
   * Launch a counter-missile aimed at (targetX, targetY) from the
   * nearest base that still has ammo. Silent + no-op if no base has
   * ammo (arcade convention — empty bases just don't fire).
   *
   * Also silent if the click is below the ground line — the player
   * shouldn't be able to bombard their own bases.
   */
  private tryLaunchCounterMissile(targetX: number, targetY: number): void {
    if (targetY >= GROUND_LINE_Y) return;
    const baseIdx = this.nearestArmedBase(targetX);
    if (baseIdx === -1) return;
    const base = BASES[baseIdx];
    if (base === undefined) return;
    this.ammo[baseIdx] = (this.ammo[baseIdx] ?? 0) - 1;
    // Launch origin = the barrel TIP, not the base center. Reuses
    // the same unit-vector math the turret render uses so the trail
    // visually emerges from where the player sees the barrel pointing.
    const pivotX = base.cx;
    const pivotY = GROUND_LINE_Y - BASE_H - 2;
    const dx = targetX - pivotX;
    const dy = targetY - pivotY;
    const flightDist = Math.hypot(dx, dy) || 1;
    const ux = dx / flightDist;
    const uy = dy / flightDist;
    const barrelTipX = pivotX + ux * 9;
    const barrelTipY = pivotY + uy * 9;
    this.missiles.push({
      x: barrelTipX,
      y: barrelTipY,
      targetX,
      targetY,
      originX: barrelTipX,
      originY: barrelTipY,
      flightDistance: Math.hypot(targetX - barrelTipX, targetY - barrelTipY) || 1,
      vx: ux * COUNTER_SPEED_PX_PER_TICK,
      vy: uy * COUNTER_SPEED_PX_PER_TICK,
      phase: 'flying',
      phaseTicks: 0,
      trail: [{ x: barrelTipX, y: barrelTipY }],
    });
    this.ctx.audio.sfx('counter-launch');
  }

  /**
   * Pick the base nearest to clickX whose ammo > 0. Returns -1 if
   * every base is empty.
   */
  private nearestArmedBase(clickX: number): number {
    let best = -1;
    let bestDist = Number.POSITIVE_INFINITY;
    for (let i = 0; i < BASES.length; i++) {
      if ((this.ammo[i] ?? 0) <= 0) continue;
      const b = BASES[i];
      if (b === undefined) continue;
      const d = Math.abs(b.cx - clickX);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    return best;
  }

  /**
   * Advance every counter-missile by one tick. Flying missiles step
   * toward target; detonate on arrival. Exploding missiles count
   * phaseTicks toward COUNTER_EXPLODE_TOTAL_TICKS, then despawn.
   */
  private tickCounterMissiles(): void {
    const survivors: CounterMissile[] = [];
    for (const m of this.missiles) {
      if (m.phase === 'flying') {
        // Linear step along the launch vector — this is the "base"
        // trajectory before the arc lift is added.
        m.x += m.vx;
        m.y += m.vy;
        // Arc lift — parameter t goes 0 (at launch) → 1 (at target),
        // measured as fraction of the original flight distance the
        // missile has covered along its origin→target line. Lift
        // peaks at t=0.5 with sin(πt), pulling Y up by COUNTER_LIFT_PX
        // at the midpoint.
        const traveled = Math.hypot(m.x - m.originX, m.y - m.originY);
        const t = Math.min(1, traveled / m.flightDistance);
        const liftY = -Math.sin(t * Math.PI) * COUNTER_LIFT_PX;
        const renderY = m.y + liftY;
        // Trail records the LIFTED position so it visually arcs.
        m.trail.unshift({ x: m.x, y: renderY });
        if (m.trail.length > COUNTER_TRAIL_TICKS) m.trail.length = COUNTER_TRAIL_TICKS;
        // Stamp the lift into the missile's render anchor so
        // drawCounterHead picks it up. Stored on m.x/m.y? No —
        // m.x/m.y track the LINEAR position so the lift parameter
        // remains correct. We override the head's render Y in the
        // head draw by re-deriving lift there... simpler: cache the
        // current lifted Y on the missile itself for the renderer.
        // Stash it onto trail[0].y, which the renderer already
        // honors as the head position.
        // (Done above by unshifting {x, renderY}.)
        // Reached target? Compare LINEAR remaining distance — the
        // lift is purely visual; arrival is when the missile has
        // covered the original origin→target line.
        if (t >= 1 || Math.hypot(m.targetX - m.x, m.targetY - m.y) <= COUNTER_SPEED_PX_PER_TICK) {
          m.x = m.targetX;
          m.y = m.targetY;
          m.phase = 'exploding';
          m.phaseTicks = 0;
          this.ctx.audio.sfx('boom');
          this.flashTicks = COUNTER_FLASH_TICKS;
        }
        survivors.push(m);
        continue;
      }
      // Exploding — phaseTicks++ until fully faded, then despawn.
      m.phaseTicks++;
      if (m.phaseTicks < COUNTER_EXPLODE_TOTAL_TICKS) {
        survivors.push(m);
      }
    }
    this.missiles = survivors;
  }

  // ---- Incoming ICBMs (step 3) ----

  /**
   * Arm the next ICBM spawn with a jittered delay. PRNG-sourced
   * jitter so spawn timing isn't memorizable across runs at the
   * tick level.
   */
  private armNextIcbmSpawn(): void {
    const jitter = this.ctx.random.int(
      -WAVE_SPAWN_JITTER_TICKS,
      WAVE_SPAWN_JITTER_TICKS
    );
    const baseGap = getWaveDifficulty(this.wave).spawnGapTicks;
    this.nextIcbmSpawnTicks = Math.max(20, baseGap + jitter);
  }

  /**
   * Advance all incoming ICBMs by one tick. Spawn timer triggers a
   * new ICBM if any remain in the wave AND the delay has elapsed.
   * Existing ICBMs descend; on city-impact they flag dead. Dead
   * ICBMs are swept at the end of the tick.
   */
  private tickIncoming(): void {
    // Spawn timer — only ticks down while we have ICBMs left to
    // spawn for this wave AND at least one city is alive.
    if (
      this.icbmsRemainingThisWave > 0 &&
      this.destroyedCities.some((d) => !d)
    ) {
      this.nextIcbmSpawnTicks--;
      if (this.nextIcbmSpawnTicks <= 0) {
        this.spawnIncoming();
        this.icbmsRemainingThisWave--;
        if (this.icbmsRemainingThisWave > 0) {
          this.armNextIcbmSpawn();
        }
      }
    }

    // Per-ICBM advance.
    const survivors: IncomingMissile[] = [];
    for (const m of this.incoming) {
      if (m.dead) continue;
      m.x += m.vx;
      m.y += m.vy;
      m.trail.unshift({ x: m.x, y: m.y });
      if (m.trail.length > ICBM_TRAIL_MAX_LEN) m.trail.length = ICBM_TRAIL_MAX_LEN;
      // Reached the city? Destroy it, spawn the big city-burst,
      // trigger screen shake, check for all-cities-down → death-pause.
      const targetCity = CITIES[m.targetCityIdx];
      if (targetCity !== undefined) {
        const cityTop = GROUND_LINE_Y - targetCity.h;
        if (m.y >= cityTop && !this.destroyedCities[m.targetCityIdx]) {
          this.destroyCity(m.targetCityIdx);
          m.dead = true;
          continue;
        }
      }
      // Fell past the ground line — target city already gone, or
      // edge case where the ICBM's target died mid-flight. Silent
      // despawn.
      if (m.y >= GROUND_LINE_Y) {
        m.dead = true;
        continue;
      }
      survivors.push(m);
    }
    this.incoming = survivors;

    // Wave-clear check — fired the tick the LAST ICBM either died,
    // hit a city, or fell past the ground. Only enters the wave-
    // clear card from 'playing' (death-pause and game-over already
    // returned out of update() before reaching here).
    if (
      this.phase === 'playing' &&
      this.icbmsRemainingThisWave <= 0 &&
      this.incoming.length === 0
    ) {
      this.enterWaveClear();
    }
  }

  /**
   * Spawn one new ICBM at a random top-edge X aimed at a random
   * LIVE city. Determinism: PRNG draws are spawnX and targetCity-
   * pick, in that fixed order, so replays reproduce.
   */
  private spawnIncoming(): void {
    const spawnX = this.ctx.random.int(0, WIDTH - 1);
    // Pick a live target city from the surviving set.
    const liveIndices: number[] = [];
    for (let i = 0; i < CITIES.length; i++) {
      if (!this.destroyedCities[i]) liveIndices.push(i);
    }
    if (liveIndices.length === 0) return;
    const pick = this.ctx.random.int(0, liveIndices.length - 1);
    const targetIdx = liveIndices[pick];
    if (targetIdx === undefined) return;
    const city = CITIES[targetIdx];
    if (city === undefined) return;
    const targetX = city.x + city.w / 2;
    const targetY = GROUND_LINE_Y - city.h;
    const dx = targetX - spawnX;
    const dy = targetY - 0;
    const len = Math.hypot(dx, dy) || 1;
    const speed = getWaveDifficulty(this.wave).icbmSpeed;
    const vx = (dx / len) * speed;
    const vy = (dy / len) * speed;
    this.incoming.push({
      x: spawnX,
      y: 0,
      targetCityIdx: targetIdx,
      vx,
      vy,
      trail: [{ x: spawnX, y: 0 }],
      dead: false,
    });
    this.ctx.audio.sfx('incoming');
  }

  // ---- Interception + city damage (step 4) ----

  /**
   * For every live ICBM, test whether its head is inside any
   * currently-LIVE counter-missile blast zone (EXPAND + HOLD ticks).
   * Each hit:
   *   - Marks the ICBM dead.
   *   - Spawns a small pink burst at the kill point.
   *   - Adds SCORE_PER_KILL to the score.
   *   - One counter-blast can chain-kill multiple ICBMs in the same
   *     tick — drift them into one big explosion.
   *
   * Determinism: this method consumes NO PRNG. Kill decisions are
   * pure geometry over deterministic state, so replays reproduce.
   */
  private tickInterception(): void {
    for (const ibm of this.incoming) {
      if (ibm.dead) continue;
      for (const cm of this.missiles) {
        if (cm.phase !== 'exploding') continue;
        // Only the live kill window counts (EXPAND + HOLD). Once the
        // explosion enters FADE, the ring is visible but no longer
        // dangerous — matches what the player sees on the kill-zone
        // outline (which only renders during the live window).
        if (cm.phaseTicks >= COUNTER_EXPLODE_EXPAND_TICKS + COUNTER_EXPLODE_HOLD_TICKS) {
          continue;
        }
        const dx = ibm.x - cm.x;
        const dy = ibm.y - cm.y;
        if (dx * dx + dy * dy <= COUNTER_BLAST_RADIUS * COUNTER_BLAST_RADIUS) {
          ibm.dead = true;
          this.score += SCORE_PER_KILL;
          this.bursts.push({
            x: ibm.x,
            y: ibm.y,
            radius: ICBM_KILL_BURST_RADIUS,
            durationTicks: ICBM_KILL_BURST_TICKS,
            age: 0,
            kind: 'icbm-kill',
          });
          break;
        }
      }
    }
  }

  /**
   * Destroy a city. Spawns the dramatic burst + triggers screen
   * shake. If this was the last living city, route to death-pause
   * (which holds the GAME OVER panel briefly before the launcher's
   * initials overlay takes over).
   */
  private destroyCity(cityIdx: number): void {
    const c = CITIES[cityIdx];
    if (c === undefined) return;
    this.destroyedCities[cityIdx] = true;
    this.bursts.push({
      x: c.x + c.w / 2,
      y: GROUND_LINE_Y - c.h / 2,
      radius: CITY_DESTROY_BURST_RADIUS,
      durationTicks: CITY_DESTROY_BURST_TICKS,
      age: 0,
      kind: 'city-destroy',
    });
    this.shakeTicks = CITY_DESTROY_SHAKE_TICKS;
    this.ctx.audio.sfx('city-destroyed');
    // All cities down? Death-pause → game-over.
    if (this.destroyedCities.every((d) => d)) {
      this.finalScore = this.score;
      this.phase = 'death-pause';
      this.phaseTicks = DEATH_PAUSE_TICKS;
      this.gameOverLockTicks = GAME_OVER_INPUT_LOCK_TICKS;
    }
  }

  /**
   * Advance + sweep bursts. Each tick the renderer reads age vs.
   * durationTicks to compute radius scale + alpha; when age reaches
   * durationTicks the burst is dropped.
   */
  private tickBursts(): void {
    const survivors: Burst[] = [];
    for (const b of this.bursts) {
      b.age++;
      if (b.age < b.durationTicks) survivors.push(b);
    }
    this.bursts = survivors;
  }

  // ---- Waves (step 5) ----

  /**
   * Wave just cleared — compute the bonus breakdown, award score,
   * switch to the WAVE CLEAR card. Step 6's win-cinematic will
   * intercept this when `wave === WIN_WAVE` instead of routing to
   * a wave-clear (you've completed the cartridge).
   */
  private enterWaveClear(): void {
    // Survival bonus: how many cities are still standing.
    const livingCities = this.destroyedCities.filter((d) => !d).length;
    this.lastSurvivalBonus = livingCities * SURVIVAL_BONUS_PER_CITY;
    // Ammo bonus: how many counter-missiles you DIDN'T use across
    // all three bases. Rewards efficient defense.
    const unusedAmmo = this.ammo.reduce((sum, a) => sum + a, 0);
    this.lastAmmoBonus = unusedAmmo * AMMO_BONUS_PER_UNUSED;
    this.score += this.lastSurvivalBonus + this.lastAmmoBonus;
    this.phase = 'wave-clear';
    this.phaseTicks = WAVE_CARD_TICKS;
  }

  /**
   * Wave-clear card dismissed — start the next wave. Refills ammo
   * (the survival challenge is per-wave; running out mid-cartridge
   * shouldn't trickle into the next wave). Re-arms the spawn timer.
   *
   * Step 6 will intercept the "advanced past WIN_WAVE" case here
   * and route to the TREASURE! cinematic instead of starting an
   * impossible 11th wave.
   */
  private advanceToNextWave(): void {
    this.wave++;
    // Step 6 will intercept this branch to fire the TREASURE! win
    // cinematic when the player has just survived the final wave.
    // Step 5 just halts at the win threshold by routing into the
    // game-over phase — temporary, replaced in step 6.
    if (this.wave > WIN_WAVE) {
      this.finalScore = this.score;
      this.phase = 'death-pause';
      this.phaseTicks = DEATH_PAUSE_TICKS;
      this.gameOverLockTicks = GAME_OVER_INPUT_LOCK_TICKS;
      return;
    }
    // Refill ammo to start-of-wave so the player gets a fresh tank
    // each wave. Bases that already had ammo top up; empty bases
    // refill to full.
    for (let i = 0; i < this.ammo.length; i++) this.ammo[i] = BASE_AMMO_START;
    // Reset wave-spawn state for the new wave.
    const diff = getWaveDifficulty(this.wave);
    this.icbmsRemainingThisWave = diff.icbmCount;
    this.armNextIcbmSpawn();
    this.phase = 'playing';
    this.phaseTicks = 0;
  }
}

// ---- Star-field generation ----

/**
 * Build the star field deterministically from the GameContext's PRNG.
 * Same seed → same star layout, so attract-mode demos are reproducible
 * and the cartridge boots identically across runs from the cabinet's
 * test scaffolding.
 */
function buildStarField(ctx: GameContext): readonly Star[] {
  const stars: Star[] = [];
  // Near stars — brighter, sparser.
  for (let i = 0; i < NEAR_STAR_COUNT; i++) {
    stars.push({
      x: ctx.random.int(0, WIDTH - 1),
      y: ctx.random.int(0, TARGET_LINE_Y - 20),
      phase: ctx.random.int(0, 360),
      near: true,
    });
  }
  // Far stars — dim, denser, also higher up.
  for (let i = 0; i < FAR_STAR_COUNT; i++) {
    stars.push({
      x: ctx.random.int(0, WIDTH - 1),
      y: ctx.random.int(0, TARGET_LINE_Y - 30),
      phase: ctx.random.int(0, 360),
      near: false,
    });
  }
  return stars;
}

// ---- Rendering ----

function drawSky(g: CanvasRenderingContext2D): void {
  const grad = g.createLinearGradient(0, 0, 0, GROUND_LINE_Y);
  grad.addColorStop(0, SKY_TOP);
  grad.addColorStop(1, SKY_HORIZON);
  g.fillStyle = grad;
  g.fillRect(0, 0, WIDTH, GROUND_LINE_Y);
}

function drawStarField(
  g: CanvasRenderingContext2D,
  stars: readonly Star[],
  worldTicks: number
): void {
  // Twinkle = per-star sine on phase + worldTicks, mapped to alpha.
  // Near stars shimmer wider amplitude than far stars so the depth
  // read is consistent across the field.
  for (const s of stars) {
    const wave = Math.sin((worldTicks + s.phase) / (s.near ? 40 : 70));
    const baseAlpha = s.near ? 0.85 : 0.45;
    const amp = s.near ? 0.15 : 0.25;
    const alpha = Math.max(0.05, baseAlpha + amp * wave);
    g.globalAlpha = alpha;
    g.fillStyle = s.near ? STAR_NEAR : STAR_FAR;
    g.fillRect(s.x, s.y, 1, 1);
  }
  g.globalAlpha = 1;
}

function drawGround(g: CanvasRenderingContext2D): void {
  const grad = g.createLinearGradient(0, GROUND_LINE_Y, 0, FOOTER_Y);
  grad.addColorStop(0, GROUND_TOP);
  grad.addColorStop(1, GROUND_BOTTOM);
  g.fillStyle = grad;
  g.fillRect(0, GROUND_LINE_Y, WIDTH, FOOTER_Y - GROUND_LINE_Y);
}

function drawCities(
  g: CanvasRenderingContext2D,
  destroyed: readonly boolean[],
  worldTicks: number
): void {
  for (let i = 0; i < CITIES.length; i++) {
    const c = CITIES[i];
    if (c === undefined) continue;
    if (destroyed[i]) {
      drawRubble(g, c);
      continue;
    }
    // Body.
    const top = GROUND_LINE_Y - c.h;
    g.fillStyle = CITY_BODY;
    g.fillRect(c.x, top, c.w, c.h);
    // Top edge highlight — 1px on top so cities have a subtle
    // parallax "lit-from-below" read against the dark sky.
    g.fillStyle = CITY_OUTLINE;
    g.fillRect(c.x, top, c.w, 1);
    // Windows. Each window flickers on its own LFO so the city
    // reads as inhabited rather than static.
    const cellW = c.w / (c.windowCols + 1);
    const cellH = c.h / (c.windowRows + 1);
    for (let wr = 0; wr < c.windowRows; wr++) {
      for (let wc = 0; wc < c.windowCols; wc++) {
        const wx = c.x + cellW * (wc + 1);
        const wy = top + cellH * (wr + 1);
        // LFO phase derived from city + window indices — same across
        // runs, gives the field a "rolling shimmer" feel without
        // PRNG draws every tick.
        const phase = i * 17 + wr * 7 + wc * 11;
        const wave = Math.sin((worldTicks + phase * 13) / 80);
        const lit = wave > -0.3; // mostly lit, brief dim moments
        g.fillStyle = lit ? WINDOW_LIT : WINDOW_DIM;
        g.fillRect(Math.floor(wx - 1), Math.floor(wy - 1), 2, 2);
      }
    }
  }
}

function drawRubble(g: CanvasRenderingContext2D, c: CitySpec): void {
  // Step 4 will own the destroyed-city render — we shell it here
  // so step 1 has the function ready when destroyedCities[i] flips.
  const top = GROUND_LINE_Y - Math.floor(c.h * 0.35);
  g.fillStyle = CITY_BODY;
  g.fillRect(c.x, top, c.w, GROUND_LINE_Y - top);
}

function drawBases(
  g: CanvasRenderingContext2D,
  ammo: readonly number[],
  cursorX: number,
  cursorY: number,
  pointerSeen: boolean
): void {
  for (let i = 0; i < BASES.length; i++) {
    const b = BASES[i];
    if (b === undefined) continue;
    const top = GROUND_LINE_Y - BASE_H;
    // Body — trapezoid-ish cyan turret housing.
    g.fillStyle = BASE_OUTLINE;
    g.fillRect(b.cx - BASE_W / 2 - 1, top - 1, BASE_W + 2, BASE_H + 2);
    g.fillStyle = BASE_BODY;
    g.fillRect(b.cx - BASE_W / 2, top, BASE_W, BASE_H);
    // Shadow side (right half darker for a "lit from upper-left" read).
    g.fillStyle = BASE_DARK;
    g.fillRect(b.cx, top, BASE_W / 2, BASE_H);
    // Ammo pips — one per remaining counter-missile, stacked above
    // the turret. 10-shot bases fit in a 5×2 grid.
    const remaining = ammo[i] ?? 0;
    for (let j = 0; j < remaining; j++) {
      const col = j % 5;
      const row = Math.floor(j / 5);
      const px = b.cx - 9 + col * 4;
      const py = top - 5 - row * 3;
      g.fillStyle = BASE_AMMO_DOT;
      g.fillRect(px, py, 2, 2);
    }
    // Turret barrel — rotates to aim at cursor if the pointer's
    // been touched. Otherwise points straight up at the sky.
    let aimX: number;
    let aimY: number;
    if (pointerSeen) {
      aimX = cursorX;
      aimY = cursorY;
    } else {
      aimX = b.cx;
      aimY = top - 40;
    }
    const pivotX = b.cx;
    const pivotY = top - 2;
    const dx = aimX - pivotX;
    const dy = aimY - pivotY;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const barrelLen = 9;
    const tipX = pivotX + ux * barrelLen;
    const tipY = pivotY + uy * barrelLen;
    // Barrel — gold, 3px thick (the previous 2px pale-cyan against
    // the cyan housing washed out from any distance).
    g.strokeStyle = BASE_TURRET;
    g.lineWidth = 3;
    g.lineCap = 'round';
    g.beginPath();
    g.moveTo(pivotX, pivotY);
    g.lineTo(tipX, tipY);
    g.stroke();
    g.lineCap = 'butt';
    // White muzzle dot — reads as "the part that fires." Sits at the
    // tip of the barrel; also gives the eye a focal point to track
    // the barrel rotation by.
    g.fillStyle = BASE_TURRET_TIP;
    g.fillRect(Math.round(tipX) - 1, Math.round(tipY) - 1, 2, 2);
    // Pivot dot — small dark dot at the barrel's base so the
    // rotation pivot reads as mechanical, not floating.
    g.fillStyle = BASE_OUTLINE;
    g.fillRect(pivotX - 1, pivotY - 1, 2, 2);
  }
}

function drawIncomingTrail(
  g: CanvasRenderingContext2D,
  m: IncomingMissile
): void {
  // Solid magenta trail along the full descent path. Render every
  // recorded point as 1px so the trajectory reads as a continuous
  // line at any zoom. Heads are drawn separately (drawIncomingHead).
  g.fillStyle = ICBM_TRAIL;
  for (const p of m.trail) {
    g.fillRect(Math.round(p.x), Math.round(p.y), 1, 1);
  }
}

function drawIncomingHead(
  g: CanvasRenderingContext2D,
  m: IncomingMissile
): void {
  // 2x2 pink leading dot — slightly bigger and brighter than the
  // trail so the player tracks the threat tip easily.
  g.fillStyle = ICBM_HEAD;
  g.fillRect(Math.round(m.x) - 1, Math.round(m.y) - 1, 2, 2);
}

function drawIcbmTargetReticles(
  g: CanvasRenderingContext2D,
  incoming: readonly IncomingMissile[],
  worldTicks: number
): void {
  // Build a Set of which city indices currently have at least one
  // ICBM inbound. Render a pulsing red bracket on each one — the
  // bracket reads as "this city is targeted" without obscuring the
  // city's own silhouette.
  const targeted = new Set<number>();
  for (const m of incoming) targeted.add(m.targetCityIdx);
  if (targeted.size === 0) return;
  // Pulse 0..1 then 1..0 over ICBM_RETICLE_PULSE_PERIOD ticks.
  const pulse = 0.4 + 0.6 * Math.abs(Math.sin(
    (worldTicks / ICBM_RETICLE_PULSE_PERIOD) * Math.PI
  ));
  for (const i of targeted) {
    const c = CITIES[i];
    if (c === undefined) continue;
    const top = GROUND_LINE_Y - c.h;
    g.strokeStyle = ICBM_TARGET_RETICLE;
    g.lineWidth = 1;
    g.globalAlpha = pulse;
    // L-bracket corners — 4px arms at each corner of the city
    // bounding box. Reads as "targeting lock" without the visual
    // weight of a full rectangle.
    const corners: ReadonlyArray<{ x: number; y: number; dx: number; dy: number }> = [
      { x: c.x - 2,        y: top - 2,           dx: 1,  dy: 1 },
      { x: c.x + c.w + 1,  y: top - 2,           dx: -1, dy: 1 },
      { x: c.x - 2,        y: GROUND_LINE_Y + 1, dx: 1,  dy: -1 },
      { x: c.x + c.w + 1,  y: GROUND_LINE_Y + 1, dx: -1, dy: -1 },
    ];
    const armLen = 4;
    for (const corner of corners) {
      g.beginPath();
      g.moveTo(corner.x, corner.y);
      g.lineTo(corner.x + corner.dx * armLen, corner.y);
      g.moveTo(corner.x, corner.y);
      g.lineTo(corner.x, corner.y + corner.dy * armLen);
      g.stroke();
    }
    g.globalAlpha = 1;
  }
}

function drawCounterTrail(
  g: CanvasRenderingContext2D,
  m: CounterMissile
): void {
  // Cyan dotted trail. Fade from full at the head to 0 at the tail.
  // Only render trail in 'flying'; once exploding, the trail snaps
  // off so the eye focuses on the fireball.
  if (m.phase !== 'flying') return;
  for (let i = 1; i < m.trail.length; i++) {
    const p = m.trail[i];
    if (p === undefined) continue;
    const alpha = 1 - i / m.trail.length;
    g.globalAlpha = alpha * 0.8;
    g.fillStyle = COUNTER_TRAIL;
    g.fillRect(Math.round(p.x), Math.round(p.y), 1, 1);
  }
  g.globalAlpha = 1;
  // Also stamp a small target marker at the destination so the
  // player sees where the missile is going to detonate even if
  // they've moved the cursor.
  g.fillStyle = COUNTER_TARGET_X;
  g.fillRect(Math.round(m.targetX) - 2, Math.round(m.targetY), 5, 1);
  g.fillRect(Math.round(m.targetX), Math.round(m.targetY) - 2, 1, 5);
}

function drawCounterHead(
  g: CanvasRenderingContext2D,
  m: CounterMissile
): void {
  // 2x2 white-hot leading dot at the LIFTED position so the head
  // visually follows the arc, not the underlying linear interp.
  // trail[0] is the freshest entry pushed by tickCounterMissiles.
  const head = m.trail[0] ?? { x: m.x, y: m.y };
  g.fillStyle = COUNTER_HEAD;
  g.fillRect(Math.round(head.x) - 1, Math.round(head.y) - 1, 2, 2);
}

function drawCounterExplosion(
  g: CanvasRenderingContext2D,
  m: CounterMissile
): void {
  // Three-band fireball: white core, gold mid, red outer. Radius
  // grows during EXPAND, holds during HOLD, alpha fades during FADE.
  // Reads as a classic Missile Command burst.
  let radius: number;
  let alpha: number;
  if (m.phaseTicks < COUNTER_EXPLODE_EXPAND_TICKS) {
    const t = m.phaseTicks / COUNTER_EXPLODE_EXPAND_TICKS;
    radius = COUNTER_BLAST_RADIUS * t;
    alpha = 1;
  } else if (
    m.phaseTicks <
    COUNTER_EXPLODE_EXPAND_TICKS + COUNTER_EXPLODE_HOLD_TICKS
  ) {
    radius = COUNTER_BLAST_RADIUS;
    alpha = 1;
  } else {
    const fadeT =
      (m.phaseTicks -
        COUNTER_EXPLODE_EXPAND_TICKS -
        COUNTER_EXPLODE_HOLD_TICKS) /
      COUNTER_EXPLODE_FADE_TICKS;
    radius = COUNTER_BLAST_RADIUS;
    alpha = Math.max(0, 1 - fadeT);
  }

  g.globalAlpha = alpha;
  // Outer red ring.
  g.fillStyle = EXPLODE_OUTER;
  drawDisc(g, m.x, m.y, radius);
  // Gold mid (75% of outer).
  g.fillStyle = EXPLODE_MID;
  drawDisc(g, m.x, m.y, radius * 0.75);
  // White core (40% of outer).
  g.fillStyle = EXPLODE_CORE;
  drawDisc(g, m.x, m.y, radius * 0.4);
  g.globalAlpha = 1;
}

function drawCounterBlastZone(
  g: CanvasRenderingContext2D,
  m: CounterMissile
): void {
  // Thin outline at the kill-zone perimeter — only visible during
  // EXPAND + HOLD (the "live" window). Fades during FADE so the
  // player learns "while the outline is on, this disc kills ICBMs".
  if (m.phaseTicks >= COUNTER_EXPLODE_EXPAND_TICKS + COUNTER_EXPLODE_HOLD_TICKS) {
    return;
  }
  g.strokeStyle = EXPLODE_OUTER;
  g.lineWidth = 1;
  g.globalAlpha = 0.6;
  g.beginPath();
  g.arc(m.x, m.y, COUNTER_BLAST_RADIUS, 0, Math.PI * 2);
  g.stroke();
  g.globalAlpha = 1;
}

function drawDisc(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number
): void {
  if (r <= 0) return;
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.fill();
}

function drawBurst(g: CanvasRenderingContext2D, b: Burst): void {
  // Burst progresses 0..1 across its duration. We do a quick expand
  // (first ~30%) then a fade. ICBM-kill bursts are pink-tinted;
  // city-destroy bursts use the white-orange-red fireball gradient
  // (bigger, more dramatic).
  const t = b.age / b.durationTicks;
  // Radius: grow fast in first 30%, hold-then-shrink slightly the
  // rest of the way so the visual reads as "punch + linger" not
  // "balloon + pop".
  let radiusFrac: number;
  if (t < 0.3) {
    radiusFrac = t / 0.3;
  } else {
    radiusFrac = 1 - (t - 0.3) * 0.3; // mild shrink during fade
  }
  const r = b.radius * radiusFrac;
  // Alpha fades out across the duration so the burst dissolves.
  const alpha = Math.max(0, 1 - t);
  g.globalAlpha = alpha;
  if (b.kind === 'icbm-kill') {
    // Pink burst — matches the ICBM trail palette family.
    g.fillStyle = ICBM_TRAIL;
    drawDisc(g, b.x, b.y, r);
    g.fillStyle = ICBM_HEAD;
    drawDisc(g, b.x, b.y, r * 0.55);
    g.fillStyle = EXPLODE_CORE;
    drawDisc(g, b.x, b.y, r * 0.25);
  } else {
    // City-destroy — big white-orange-red fireball.
    g.fillStyle = EXPLODE_OUTER;
    drawDisc(g, b.x, b.y, r);
    g.fillStyle = EXPLODE_MID;
    drawDisc(g, b.x, b.y, r * 0.75);
    g.fillStyle = EXPLODE_CORE;
    drawDisc(g, b.x, b.y, r * 0.4);
  }
  g.globalAlpha = 1;
}

function drawWaveClearCard(
  g: CanvasRenderingContext2D,
  wave: number,
  survivalBonus: number,
  ammoBonus: number,
  totalScore: number,
  elapsed: number
): void {
  // Scrim behind the card so the playfield dims out under it.
  g.fillStyle = 'rgba(0, 0, 0, 0.55)';
  g.fillRect(0, 0, WIDTH, HEIGHT);

  // Card. Sized to fit heading + 2 bonus lines + total.
  const cw = 240;
  const ch = 100;
  const cx = (WIDTH - cw) / 2;
  const cy = (HEIGHT - ch) / 2;
  g.fillStyle = CARD_BG;
  g.fillRect(cx, cy, cw, ch);
  g.strokeStyle = CARD_BORDER;
  g.lineWidth = 2;
  g.strokeRect(cx + 0.5, cy + 0.5, cw - 1, ch - 1);

  g.font = '14px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  g.fillStyle = HUD_SCORE;
  g.fillText(`WAVE ${String(wave).padStart(2, '0')} CLEAR`, cx + cw / 2, cy + 18);

  // Bonus reveal — survival appears at ~20 ticks, ammo at ~40 ticks,
  // total at ~60. Reads as the bonuses being tallied up.
  g.font = '9px monospace';
  if (elapsed > 20) {
    g.textAlign = 'left';
    g.fillStyle = HUD_LABEL;
    g.fillText('SURVIVAL BONUS', cx + 12, cy + 40);
    g.textAlign = 'right';
    g.fillStyle = CARD_BORDER;
    g.fillText(
      `+${survivalBonus}`,
      cx + cw - 18,
      cy + 40
    );
  }
  if (elapsed > 40) {
    g.textAlign = 'left';
    g.fillStyle = HUD_LABEL;
    g.fillText('UNUSED AMMO BONUS', cx + 12, cy + 56);
    g.textAlign = 'right';
    g.fillStyle = CARD_BORDER;
    g.fillText(`+${ammoBonus}`, cx + cw - 18, cy + 56);
  }
  if (elapsed > 60) {
    // Total line — thin divider above, gold numerals below.
    g.strokeStyle = ATTRACT_DIM;
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(cx + 12, cy + 70);
    g.lineTo(cx + cw - 12, cy + 70);
    g.stroke();
    g.fillStyle = HUD_LABEL;
    g.textAlign = 'left';
    g.font = '9px monospace';
    g.fillText('TOTAL SCORE', cx + 12, cy + 84);
    g.textAlign = 'right';
    g.fillStyle = HUD_SCORE;
    g.font = '11px monospace';
    g.fillText(
      String(totalScore).padStart(6, '0'),
      cx + cw - 12,
      cy + 84
    );
  }

  g.textAlign = 'left';
}

function drawGameOver(
  g: CanvasRenderingContext2D,
  finalScore: number,
  phaseTicks: number,
  lockTicks: number
): void {
  // CRT-glitch intro: scan-line noise wipes over the canvas, then
  // settles into a steady-state low-intensity glitch as the player
  // reads their score. Mirrors CUBE JUMP / JADE TUNNEL.
  const introProgress = Math.min(1, phaseTicks / GLITCH_INTRO_TICKS);

  g.fillStyle = `rgba(0, 0, 0, ${0.7 * introProgress})`;
  g.fillRect(0, 0, WIDTH, HEIGHT);

  // Scan-line tears at random-ish heights.
  const tearCount = Math.floor(
    8 * introProgress * (phaseTicks < GLITCH_INTRO_TICKS ? 1 : 0.35)
  );
  for (let i = 0; i < tearCount; i++) {
    const seed = (phaseTicks * 13 + i * 71) % 270;
    const tearY = seed;
    const tearH = 1 + ((phaseTicks + i) % 4);
    const colorPick = (phaseTicks + i) % 3;
    const tearColor =
      colorPick === 0
        ? 'rgba(0, 240, 200, 0.45)'
        : colorPick === 1
          ? 'rgba(255, 48, 136, 0.4)'
          : 'rgba(255, 234, 0, 0.35)';
    g.fillStyle = tearColor;
    g.fillRect(0, tearY, WIDTH, tearH);
  }

  // Panel — centered card with red border + "DEFENSE FAILED" heading.
  const cw = 240;
  const ch = 88;
  const cx = (WIDTH - cw) / 2;
  const cy = (HEIGHT - ch) / 2;
  if (introProgress >= 0.5) {
    g.fillStyle = CARD_BG;
    g.fillRect(cx, cy, cw, ch);
    g.strokeStyle = ICBM_TARGET_RETICLE;
    g.lineWidth = 2;
    g.strokeRect(cx + 0.5, cy + 0.5, cw - 1, ch - 1);

    g.font = '14px monospace';
    g.textBaseline = 'middle';
    g.textAlign = 'center';
    g.fillStyle = ICBM_TARGET_RETICLE;
    g.fillText('DEFENSE FAILED', cx + cw / 2, cy + 18);

    g.font = '10px monospace';
    g.fillStyle = HUD_LABEL;
    g.fillText('FINAL SCORE', cx + cw / 2, cy + 38);
    g.font = '14px monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(String(finalScore).padStart(6, '0'), cx + cw / 2, cy + 54);

    // PRESS Z prompt — dim during input lock, blinking after.
    const promptLive = lockTicks <= 0;
    const promptBlink = Math.floor(phaseTicks / 24) % 2 === 0;
    if (promptLive && promptBlink) {
      g.fillStyle = CARD_BORDER;
      g.font = '8px monospace';
      g.fillText('PRESS Z TO RESTART', cx + cw / 2, cy + 74);
    } else if (!promptLive) {
      g.fillStyle = ATTRACT_DIM;
      g.font = '8px monospace';
      g.fillText('. . .', cx + cw / 2, cy + 74);
    }

    g.textAlign = 'left';
  }
}

function drawCrosshair(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  worldTicks: number
): void {
  // Pulsing pale-cyan reticle ring + small center dot + four tick
  // marks on the cardinals. Pulse modulates ring radius slightly so
  // the cursor reads as "live" not static.
  const pulse = 0.5 + 0.5 * Math.sin((worldTicks / CURSOR_PULSE_PERIOD) * Math.PI * 2);
  const r = CURSOR_RING_R + pulse * 1.5;
  // Outer ring.
  g.strokeStyle = CURSOR_BODY;
  g.lineWidth = 1;
  g.beginPath();
  g.arc(x, y, r, 0, Math.PI * 2);
  g.stroke();
  // Inner pulse flash — white, lower alpha.
  g.globalAlpha = 0.5 + 0.5 * pulse;
  g.strokeStyle = CURSOR_PULSE;
  g.lineWidth = 1;
  g.beginPath();
  g.arc(x, y, r - 3, 0, Math.PI * 2);
  g.stroke();
  g.globalAlpha = 1;
  // Cardinal ticks.
  g.fillStyle = CURSOR_BODY;
  g.fillRect(x - r - 4, y, 3, 1);
  g.fillRect(x + r + 1, y, 3, 1);
  g.fillRect(x, y - r - 4, 1, 3);
  g.fillRect(x, y + r + 1, 1, 3);
  // Center dot.
  g.fillRect(x, y, 1, 1);
}

function drawSentinelHint(g: CanvasRenderingContext2D, worldTicks: number): void {
  // Centered card prompting the player to move the mouse / tap.
  // Blinks on a slow cadence so it draws the eye.
  const blink = Math.floor(worldTicks / 30) % 2 === 0;
  const cw = 200;
  const ch = 40;
  const cx = (WIDTH - cw) / 2;
  const cy = HEIGHT / 2 - ch / 2;
  g.fillStyle = CURSOR_HINT_BG;
  g.fillRect(cx, cy, cw, ch);
  g.strokeStyle = CURSOR_HINT_BORDER;
  g.lineWidth = 1;
  g.strokeRect(cx + 0.5, cy + 0.5, cw - 1, ch - 1);
  g.font = '10px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  g.fillStyle = blink ? CURSOR_HINT_TEXT : ATTRACT_DIM;
  g.fillText('MOVE MOUSE TO AIM', cx + cw / 2, cy + 14);
  g.font = '7px monospace';
  g.fillStyle = ATTRACT_PROMPT;
  g.fillText('(TAP ON TOUCH)', cx + cw / 2, cy + 28);
  g.textAlign = 'left';
}

function drawAttractBanner(g: CanvasRenderingContext2D, worldTicks: number): void {
  // Bottom-center "DEMO PLAY" pill that blinks so the player reads
  // "I'm not playing — the cabinet is showing me a demo."
  const blink = Math.floor(worldTicks / 30) % 2 === 0;
  if (!blink) return;
  const text = 'DEMO PLAY';
  g.font = '8px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  const w = g.measureText(text).width + 16;
  const h = 14;
  const x = (WIDTH - w) / 2;
  const y = HEIGHT - 18;
  g.fillStyle = CARD_BG;
  g.fillRect(x, y, w, h);
  g.strokeStyle = ATTRACT_TITLE;
  g.lineWidth = 1;
  g.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
  g.fillStyle = ATTRACT_PROMPT;
  g.fillText(text, WIDTH / 2, y + h / 2);
  g.textAlign = 'left';
}

function drawHudTop(
  g: CanvasRenderingContext2D,
  score: number,
  wave: number,
  destroyed: readonly boolean[]
): void {
  g.fillStyle = CARD_BG;
  g.fillRect(0, 0, WIDTH, HUD_TOP_H);
  g.fillStyle = CARD_BORDER;
  g.fillRect(0, HUD_TOP_H - 1, WIDTH, 1);

  g.font = '8px monospace';
  g.textBaseline = 'middle';

  // SCORE (left).
  g.fillStyle = HUD_LABEL;
  g.fillText('SCORE', 8, HUD_TOP_H / 2 - 4);
  g.fillStyle = HUD_SCORE;
  g.font = '12px monospace';
  g.fillText(String(score).padStart(6, '0'), 8, HUD_TOP_H / 2 + 5);

  // WAVE (center).
  g.font = '8px monospace';
  g.textBaseline = 'middle';
  g.textAlign = 'center';
  g.fillStyle = HUD_LABEL;
  g.fillText('WAVE', WIDTH / 2, HUD_TOP_H / 2 - 4);
  g.fillStyle = HUD_WAVE;
  g.font = '12px monospace';
  g.fillText(String(wave).padStart(2, '0'), WIDTH / 2, HUD_TOP_H / 2 + 5);

  // CITIES REMAINING (right).
  g.font = '8px monospace';
  g.textAlign = 'right';
  g.fillStyle = HUD_LABEL;
  g.fillText('CITIES', WIDTH - 8, HUD_TOP_H / 2 - 4);
  // Render 6 mini-city pips, lit = alive.
  for (let i = 0; i < CITIES.length; i++) {
    const x = WIDTH - 8 - (CITIES.length - 1 - i) * 7;
    g.fillStyle = destroyed[i] ? '#3a1838' : HUD_CITIES_LIT;
    g.fillRect(x - 3, HUD_TOP_H / 2 + 2, 4, 6);
  }
  g.textAlign = 'left';
}
